import { memo, useState } from "react";
import { AlertTriangle, Code2, Edit3, Play, Sparkles, Square } from "lucide-react";
import { Button } from "@/components/atoms/studio/ui/button";
import type { IntentContract, PlanContract, TestPlan } from "@/services/testing";
import { planExecutionPreview } from "@/utils/testing/planPreview";
import { RunBlockedNote } from "./RunBlockedNote";
import { IntentPanel } from "./IntentPanel";
import { PlanDiffSummary } from "./TestingRepairCards";
import { PlanExecutionPreview } from "./PlanExecutionPreview";
import { PlanSpecMonacoPreview } from "./PlanSpecMonacoPreview";
import { VerificationBadge } from "./PlanStatusBadge";

export interface PlanProposalCardProps {
	plan: TestPlan;
	planContract?: PlanContract;
	intentContract?: IntentContract;
	intentGaps?: string[];
	/** Diff summary attached when this card is an UPDATE to an earlier proposal. */
	planDiff?: Record<string, unknown>;

	running?: boolean;
	stopping?: boolean;
	planningActive?: boolean;

	onRun?: () => void;
	onStop?: () => void;
	onEdit?: () => void;
	onEditStep?: (stepIdx: number, newSay: string) => Promise<void>;
	onVerify?: (planId: string) => void;
	verifying?: boolean;
	/** The server refused this plan at execute time (contract validation 409). Read-only. */
	runBlockedReason?: string | null;
}

/**
 * The proposed test plan: what the agent understood, what it will do, and the controls to run it.
 *
 * Extracted from ChatBubble, which had grown to render the user bubble, the assistant reply, the
 * tool accordion, the token counter AND this entire card in one 350-line component. Splitting it
 * lets the turn renderer place each piece where it belongs and lets this card be memoized on its own
 * inputs — it is the most expensive thing in the thread (preview table + optional Monaco).
 *
 * Behaviour is unchanged; the layout is tightened: the header wraps instead of squeezing badges, and
 * the action row groups primary/secondary rather than pushing everything to opposite edges.
 */
export const PlanProposalCard = memo(function PlanProposalCard({
	plan,
	planContract,
	intentContract,
	intentGaps,
	planDiff,
	running,
	stopping,
	planningActive,
	onRun,
	onStop,
	onEdit,
	onEditStep,
	onVerify,
	verifying,
	runBlockedReason,
}: PlanProposalCardProps) {
	const [specOpen, setSpecOpen] = useState(false);
	const preview = planExecutionPreview(plan, planContract);
	const schemaNotes = (plan.steps as { schema_notes?: string[] } | undefined)?.schema_notes;
	const destructiveCount = plan.destructive_actions?.length ?? 0;

	return (
		<div
			data-testid="chat-plan-card"
			className="overflow-hidden rounded-lg border border-primary/35 bg-card/70 text-foreground shadow-sm"
		>
			{/* Header — wraps rather than compressing the badges into unreadable slivers on a narrow
			    chat column, which is what the old single-row `justify-between` did. */}
			<div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 border-b border-border/50 bg-muted/25 px-3 py-2.5">
				<div className="flex min-w-0 flex-col gap-0.5">
					<span className="truncate text-[14px] font-semibold leading-tight">
						{plan.name || "Proposed plan"}
					</span>
					<span className="font-mono text-[11px] text-muted-foreground">
						{Array.isArray(plan.steps)
							? `${plan.steps.length} step${plan.steps.length === 1 ? "" : "s"}`
							: "spec ready"}
					</span>
				</div>
				<div className="flex flex-wrap items-center gap-1.5">
					{/* Destructive-plan indicator — a heads-up BEFORE Run; the run itself is gated by a
					    confirmation dialog. Safe plans show nothing. */}
					{destructiveCount > 0 && (
						<span
							className="inline-flex items-center gap-1 rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-[11px] font-medium text-destructive-strong"
							title={plan.destructive_actions!.map((a) => a.label).join(" · ")}
						>
							<AlertTriangle className="h-2.5 w-2.5" />
							Changes data
						</span>
					)}
					<VerificationBadge plan={plan} onVerify={onVerify} verifying={verifying} />
					<span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-medium text-primary">
						{plan.status || "proposed"}
					</span>
				</div>
			</div>

			<div className="space-y-2 p-3">
				{/* Grounding-degraded banner: org metadata was unavailable during planning, so field names
				    couldn't be verified. Be honest rather than implying a fully-grounded plan. */}
				{planContract?.confidence === "unverified" && (
					<p className="flex items-start gap-1.5 rounded border border-warning/40 bg-warning/10 p-2 text-[11px] leading-relaxed text-warning-strong">
						<AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
						<span>
							Built without org metadata — field names couldn't be verified against your org (the
							metadata service was unavailable). The plan is runnable; the run will confirm it.
						</span>
					</p>
				)}

				{planDiff && <PlanDiffSummary diff={planDiff} />}

				{/* What the planner understood, and what it could NOT pin down — before the approval
				    controls, so the user reads it first. Advisory only. */}
				<IntentPanel contract={intentContract} gaps={intentGaps} />

				<PlanExecutionPreview
					preview={preview}
					plan={plan}
					disabled={Boolean(planningActive || running)}
					onEditStep={onEditStep}
				/>

				{/* Auto-tuning the planner applied to fit this org, shown BEFORE the run so the user sees
				    what was adjusted and isn't surprised by it afterwards. */}
				{schemaNotes && schemaNotes.length > 0 && (
					<details className="rounded border border-primary/20 bg-primary/5 text-[11px] text-foreground/80">
						<summary className="flex cursor-pointer select-none items-center gap-1 p-1.5 font-semibold text-primary">
							<Sparkles className="h-3 w-3" />
							{schemaNotes.length} auto-adjustment{schemaNotes.length > 1 ? "s" : ""} to match your org
						</summary>
						<ul className="ml-5 list-disc space-y-0.5 px-2 pb-2 opacity-90">
							{schemaNotes.map((note, i) => (
								<li key={i}>{note}</li>
							))}
						</ul>
					</details>
				)}

				{/* The execute route's contract validation refused this plan. The server's own message
				    says what is wrong; the fix is a re-plan in chat, so there is nothing to press. */}
				{runBlockedReason && !running && <RunBlockedNote reason={runBlockedReason} />}
			</div>

			{/* Actions — primary group left, utilities right, wrapping as one row on a narrow column. */}
			<div className="flex flex-wrap items-center gap-1.5 border-t border-border/50 bg-muted/20 px-3 py-2">
				{running ? (
					<Button
						size="sm"
						variant="destructive"
						className="h-7 text-xs"
						onClick={onStop}
						disabled={stopping}
						title={stopping ? "Stopping the run…" : "Cancel the running test"}
					>
						<Square className="mr-1 h-3 w-3" />
						{stopping ? "Stopping…" : "Cancel"}
					</Button>
				) : (
					onRun && (
						<>
							<Button
								size="sm"
								className="h-7 text-xs"
								onClick={onRun}
								disabled={planningActive}
								title="Run this test in a live browser"
							>
								<Play className="mr-1 h-3 w-3 fill-current" />
								Run
							</Button>
							{onEdit && (
								<Button
									size="sm"
									variant="outline"
									className="h-7 text-xs"
									onClick={onEdit}
									disabled={planningActive}
									title="Edit this plan — your next message updates it instead of creating a new one"
								>
									<Edit3 className="mr-1 h-3 w-3" />
									Edit
								</Button>
							)}
						</>
					)
				)}
				<Button
					size="sm"
					variant="ghost"
					className="ml-auto h-7 text-xs text-muted-foreground"
					onClick={() => setSpecOpen((v) => !v)}
					aria-expanded={specOpen}
					title="Show the compiled Playwright spec"
				>
					<Code2 className="mr-1 h-3 w-3" />
					{specOpen ? "Hide spec" : "Spec"}
				</Button>
			</div>

			{specOpen && <PlanSpecMonacoPreview plan={plan} />}
		</div>
	);
});
